import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';

/**
 * Hides destructive controls from users the backend identified as `regular`.
 *
 * Everyone else — admins, and anyone whose role could not be resolved — sees
 * the control exactly as before. Hiding rather than disabling keeps the
 * surrounding table layout unchanged for read-only accounts.
 */
export function AdminAction({ children }: { children: ReactNode }) {
  const { canDestroy } = useAuth();
  return canDestroy ? <>{children}</> : null;
}

/**
 * Route guard for the admin board. Uses the same fail-open rule as
 * `AdminAction` so the board stays reachable before the first admin has been
 * appointed, or when the users endpoint is unavailable.
 */
export function AdminOnly({ children }: { children: ReactNode }) {
  const { canDestroy, roleLoading } = useAuth();

  if (roleLoading) {
    return (
      <div
        className="flex min-h-[12rem] items-center justify-center"
        role="status"
        aria-live="polite"
      >
        <span
          className="h-7 w-7 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600"
          aria-hidden="true"
        />
        <span className="sr-only">Loading permissions</span>
      </div>
    );
  }

  return canDestroy ? <>{children}</> : <Navigate to="/lightning/overview" replace />;
}

export function ReadOnlyIndicator() {
  const { role, roleLoading } = useAuth();

  if (roleLoading || role !== 'regular') return null;

  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700"
      title="Regular accounts can view everything but cannot kill jobs or delete data"
    >
      <svg
        className="h-3.5 w-3.5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4"
        />
      </svg>
      Read-only
    </span>
  );
}
